import React, { useState, useEffect } from "react";
import {
  CalendarDays,
  CheckCircle2,
  AlertTriangle,
  ArrowLeft,
  Loader2,
  UserRound,
  Clock,
} from "lucide-react";

import api from "../../services/api";
import AppointmentSlotGrid from "../../components/AppointmentSlotGrid";

// Página pública — se abre sin sesión (link compartido por la empresa, o
// desde su web). Autoriza a crear una cita, a diferencia de /cita/:token que
// solo gestiona una que ya existe.

const ERROR_MESSAGES = {
  ERR_APPOINTMENT_SLOT_UNAVAILABLE:
    "Ese horario se acaba de ocupar. Elige otro, por favor.",
  ERR_BOOKING_MISSING_CONTACT_DATA: "Completa tu nombre y teléfono.",
};

const describeError = (err) =>
  ERROR_MESSAGES[err?.response?.data?.error] ||
  "No pudimos agendar tu cita. Intenta de nuevo en unos minutos.";

const formatTime = (iso, timeZone) =>
  new Intl.DateTimeFormat("es-ES", {
    timeZone,
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  }).format(new Date(iso));

const formatDate = (iso, timeZone) =>
  new Intl.DateTimeFormat("es-ES", {
    timeZone,
    weekday: "long",
    day: "numeric",
    month: "long",
  }).format(new Date(iso));

const StepHeader = ({ title, onBack }) => (
  <div className="flex items-center gap-2 px-5 py-4 border-b border-slate-100 dark:border-slate-800">
    {onBack && (
      <button
        onClick={onBack}
        className="p-1 rounded-lg text-slate-400 hover:text-slate-600 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
      >
        <ArrowLeft className="w-4 h-4" />
      </button>
    )}
    <p className="text-base font-semibold text-slate-900 dark:text-slate-100">
      {title}
    </p>
  </div>
);

const PublicBooking = () => {
  const [loadingOptions, setLoadingOptions] = useState(true);
  const [reasons, setReasons] = useState([]);
  const [professionals, setProfessionals] = useState([]);

  // reason -> professional -> slots -> contact -> done
  const [step, setStep] = useState("reason");
  const [selectedReason, setSelectedReason] = useState(null);
  const [selectedProfessional, setSelectedProfessional] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);

  const [slotsData, setSlotsData] = useState(null);
  const [loadingSlots, setLoadingSlots] = useState(false);

  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [appointment, setAppointment] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const [reasonsRes, professionalsRes] = await Promise.all([
          api.get("/public/booking/reasons"),
          api.get("/public/booking/professionals"),
        ]);
        setReasons(reasonsRes.data || []);
        setProfessionals(professionalsRes.data || []);

        if ((reasonsRes.data || []).length === 0) {
          setStep(
            (professionalsRes.data || []).length > 0 ? "professional" : "slots"
          );
        }
      } catch (err) {
        setError(describeError(err));
      }
      setLoadingOptions(false);
    };
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchSlots = async (professionalId, durationMinutes) => {
    setStep("slots");
    setLoadingSlots(true);
    setError(null);
    try {
      const { data } = await api.get("/public/booking/slots", {
        params: { professionalId: professionalId ?? undefined, durationMinutes, days: 14 },
      });
      setSlotsData(data);
    } catch (err) {
      setError(describeError(err));
      setSlotsData({ days: [], hasAvailability: false });
    }
    setLoadingSlots(false);
  };

  const chooseReason = (reason) => {
    setSelectedReason(reason);
    if (professionals.length > 0) {
      setStep("professional");
    } else {
      fetchSlots(null, reason?.defaultDurationMinutes || 30);
    }
  };

  const chooseProfessional = (professional) => {
    setSelectedProfessional(professional);
    fetchSlots(professional.id, selectedReason?.defaultDurationMinutes || 30);
  };

  const chooseSlot = (slotStart) => {
    setSelectedSlot(slotStart);
    setStep("contact");
  };

  const handleSubmit = async () => {
    if (!name.trim() || !phone.trim()) {
      setError("Completa tu nombre y teléfono.");
      return;
    }
    setSubmitting(true);
    setError(null);
    try {
      const { data } = await api.post("/public/booking", {
        name,
        phone,
        professionalId: selectedProfessional?.id ?? null,
        reasonId: selectedReason?.id ?? null,
        scheduledAt: selectedSlot,
      });
      setAppointment(data);
      setStep("done");
    } catch (err) {
      setError(describeError(err));
      if (err?.response?.data?.error === "ERR_APPOINTMENT_SLOT_UNAVAILABLE") {
        fetchSlots(
          selectedProfessional?.id ?? null,
          selectedReason?.defaultDurationMinutes || 30
        );
        setStep("slots");
      }
    }
    setSubmitting(false);
  };

  const goBackToReason = () => {
    setError(null);
    setStep(reasons.length > 0 ? "reason" : "professional");
  };

  const goBackToProfessional = () => {
    setError(null);
    setStep(professionals.length > 0 ? "professional" : "reason");
  };

  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-slate-100 dark:bg-slate-950 px-4 py-10">
      <div className="w-full max-w-md">
        <div className="rounded-2xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 shadow-lg overflow-hidden">
          {loadingOptions ? (
            <div className="flex flex-col items-center justify-center py-20 gap-3">
              <Loader2 className="w-6 h-6 animate-spin text-indigo-500" />
              <p className="text-sm text-slate-400">Cargando...</p>
            </div>
          ) : step === "done" ? (
            <>
              <div className="px-6 py-7 text-center">
                <div className="w-14 h-14 rounded-full bg-emerald-50 dark:bg-emerald-950/40 flex items-center justify-center mx-auto mb-3">
                  <CheckCircle2 className="w-7 h-7 text-emerald-600" />
                </div>
                <p className="text-base font-semibold text-slate-900 dark:text-slate-100">
                  ¡Tu cita quedó agendada!
                </p>
                <div className="mt-4 space-y-2 text-left bg-slate-50 dark:bg-slate-800/30 rounded-xl p-4">
                  <p className="text-sm text-slate-700 dark:text-slate-300 capitalize">
                    <CalendarDays className="w-4 h-4 inline mr-2 text-indigo-500" />
                    {formatDate(appointment.scheduledAt, appointment.timeZone)}
                  </p>
                  <p className="text-sm text-slate-700 dark:text-slate-300">
                    <Clock className="w-4 h-4 inline mr-2 text-indigo-500" />
                    {formatTime(appointment.scheduledAt, appointment.timeZone)} ·{" "}
                    {appointment.durationMinutes} min
                  </p>
                  {appointment.professional && (
                    <p className="text-sm text-slate-700 dark:text-slate-300">
                      <UserRound className="w-4 h-4 inline mr-2 text-indigo-500" />
                      {appointment.professional}
                    </p>
                  )}
                </div>
                <p className="text-xs text-slate-400 mt-4">
                  Te enviamos la confirmación por WhatsApp.
                </p>
              </div>
            </>
          ) : (
            <>
              {step === "reason" && (
                <>
                  <StepHeader title="¿Qué necesitas?" />
                  <div className="px-5 py-4 space-y-2 max-h-[60vh] overflow-y-auto">
                    {reasons.map((reason) => (
                      <button
                        key={reason.id}
                        onClick={() => chooseReason(reason)}
                        className="w-full flex items-center justify-between px-4 py-3 text-sm font-medium text-left text-slate-700 dark:text-slate-200 border border-slate-200 dark:border-slate-700 rounded-xl hover:border-indigo-500 hover:text-indigo-600 transition-colors"
                      >
                        {reason.name}
                        <span className="text-xs text-slate-400">
                          {reason.defaultDurationMinutes} min
                        </span>
                      </button>
                    ))}
                  </div>
                </>
              )}

              {step === "professional" && (
                <>
                  <StepHeader
                    title="¿Con quién prefieres?"
                    onBack={reasons.length > 0 ? goBackToReason : undefined}
                  />
                  <div className="px-5 py-4 space-y-2 max-h-[60vh] overflow-y-auto">
                    {professionals.map((professional) => (
                      <button
                        key={professional.id ?? "any"}
                        onClick={() => chooseProfessional(professional)}
                        className="w-full flex items-center gap-2 px-4 py-3 text-sm font-medium text-left text-slate-700 dark:text-slate-200 border border-slate-200 dark:border-slate-700 rounded-xl hover:border-indigo-500 hover:text-indigo-600 transition-colors"
                      >
                        <UserRound className="w-4 h-4 text-indigo-500" />
                        {professional.name}
                      </button>
                    ))}
                  </div>
                </>
              )}

              {step === "slots" && (
                <>
                  <StepHeader
                    title="Elige un horario"
                    onBack={
                      professionals.length > 0
                        ? goBackToProfessional
                        : reasons.length > 0
                        ? goBackToReason
                        : undefined
                    }
                  />
                  <div className="px-5 py-4 max-h-[60vh] overflow-y-auto">
                    {error && (
                      <p className="mb-3 text-sm text-red-500 text-center">{error}</p>
                    )}
                    <AppointmentSlotGrid
                      loading={loadingSlots}
                      days={slotsData?.days}
                      timeZone={slotsData?.timeZone}
                      onPickSlot={chooseSlot}
                      emptyMessage={
                        slotsData && !slotsData.hasAvailability
                          ? "No hay horarios publicados en este momento. Escríbenos por WhatsApp."
                          : undefined
                      }
                    />
                  </div>
                </>
              )}

              {step === "contact" && (
                <>
                  <StepHeader
                    title="Tus datos"
                    onBack={() => {
                      setError(null);
                      setStep("slots");
                    }}
                  />
                  <div className="px-5 py-4 space-y-3">
                    {error && (
                      <p className="text-sm text-red-500 text-center">{error}</p>
                    )}
                    <div>
                      <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1.5">
                        Nombre
                      </label>
                      <input
                        type="text"
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        autoFocus
                        className="w-full px-3 py-2.5 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      />
                    </div>
                    <div>
                      <label className="block text-xs font-bold text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1.5">
                        Teléfono (WhatsApp)
                      </label>
                      <input
                        type="tel"
                        value={phone}
                        onChange={(e) => setPhone(e.target.value)}
                        placeholder="+1 809 123 4567"
                        className="w-full px-3 py-2.5 text-sm border border-slate-200 dark:border-slate-700 rounded-lg bg-white dark:bg-slate-800 text-slate-900 dark:text-slate-100 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
                      />
                    </div>
                    <button
                      onClick={handleSubmit}
                      disabled={submitting}
                      className="w-full flex items-center justify-center gap-2 py-3.5 text-base font-semibold bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 disabled:opacity-50 transition-colors shadow-sm"
                    >
                      {submitting && <Loader2 className="w-5 h-5 animate-spin" />}
                      Confirmar cita
                    </button>
                  </div>
                </>
              )}

              {error && step !== "slots" && step !== "contact" && (
                <div className="px-6 pb-5 flex items-start gap-2 text-sm text-red-500">
                  <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" />
                  {error}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PublicBooking;
